import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { HelpPanel } from '../components/ui/help-panel';
import { reportsHelp } from '../lib/helpContent';
import TaxMapperSuggestionsModal from '../components/cfobuck/TaxMapperSuggestionsModal';
import { 
  CalendarIcon,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  FileText,
  BarChart3,
  PieChart,
  Scale,
  Download,
  Printer,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Clock,
  Building2,
  DollarSign,
  CreditCard,
  Users,
  AlertCircle,
  Lock,
  Sparkles,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { format, subYears, subMonths, startOfYear, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';
import {
  exportPnLToPDF,
  exportPnLToExcel,
  exportBalanceSheetToPDF,
  exportBalanceSheetToExcel,
  exportAgingToPDF,
  exportAgingToExcel,
  exportTrialBalanceToPDF,
  exportTrialBalanceToExcel,
  exportTableToPDF,
  exportTableToExcel,
  exportGeneralLedgerToPDF,
  exportGeneralLedgerToExcel,
  exportCashFlowToPDF,
  exportCashFlowToExcel,
  exportBudgetActualToPDF,
  exportBudgetActualToExcel,
  exportExpenseByVendorToPDF,
  exportExpenseByVendorToExcel,
  exportReportToCSV,
  printReport,
  generatePrintTableHTML
} from '../lib/exportUtils';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Professional currency formatting
const formatCurrency = (amount, showSign = false) => {
  const absAmount = Math.abs(amount || 0);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(absAmount);
  
  if (showSign && amount !== 0) {
    return amount < 0 ? `(${formatted})` : formatted;
  }
  return amount < 0 ? `(${formatted})` : formatted;
};

// Calculate change between two values
const calculateChange = (current, previous) => {
  if (!previous || previous === 0) return { amount: current, percent: null };
  const amount = current - previous;
  const percent = ((current - previous) / Math.abs(previous)) * 100;
  return { amount, percent };
};

// Report card component for hub
const ReportCard = ({ icon: Icon, title, description, color, onClick, comingSoon }) => (
  <Card 
    className={cn(
      "cursor-pointer transition-all hover:shadow-lg hover:border-primary/30",
      comingSoon && "opacity-60 cursor-not-allowed"
    )}
    onClick={comingSoon ? undefined : onClick}
    data-testid={`report-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center",
          color === 'blue' && "bg-blue-500/10",
          color === 'emerald' && "bg-emerald-500/10",
          color === 'violet' && "bg-violet-500/10",
          color === 'amber' && "bg-amber-500/10",
          color === 'rose' && "bg-rose-500/10"
        )}>
          <Icon className={cn(
            "w-6 h-6",
            color === 'blue' && "text-blue-500",
            color === 'emerald' && "text-emerald-500",
            color === 'violet' && "text-violet-500",
            color === 'amber' && "text-amber-500",
            color === 'rose' && "text-rose-500"
          )} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{title}</h3>
            {comingSoon && (
              <Badge variant="secondary" className="text-[10px]">
                <Lock className="w-3 h-3 mr-1" />
                Coming Soon
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </div>
    </CardContent>
  </Card>
);

// Report Section Header
const SectionHeader = ({ title, level = 1 }) => (
  <tr className={cn(
    "bg-muted/30",
    level === 1 && "font-semibold text-sm",
    level === 2 && "font-medium text-sm pl-4"
  )}>
    <td colSpan={5} className={cn("py-2 px-4", level === 2 && "pl-8")}>
      {title}
    </td>
  </tr>
);

// Report Row Component
const ReportRow = ({ 
  code, 
  name, 
  amount, 
  previousAmount, 
  showComparison,
  isSubAccount,
  isSubtotal,
  isGrandTotal,
  expanded,
  hasChildren,
  onToggle,
  level = 0
}) => {
  const change = showComparison ? calculateChange(amount, previousAmount) : null;
  
  return (
    <tr className={cn(
      "hover:bg-muted/20 transition-colors",
      isSubtotal && "bg-muted/20 font-medium",
      isGrandTotal && "bg-muted/50 font-bold border-t-2 border-b-2 border-foreground/20"
    )}>
      {/* Account Name */}
      <td className={cn(
        "py-2 px-4",
        level === 1 && "pl-8",
        level === 2 && "pl-12"
      )}>
        <div className="flex items-center gap-2">
          {hasChildren && (
            <button 
              onClick={onToggle}
              className="p-0.5 hover:bg-muted rounded"
            >
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
          {code && <span className="font-mono text-xs text-muted-foreground">{code}</span>}
          <span className={cn(isSubAccount && "text-muted-foreground")}>{name}</span>
        </div>
      </td>
      
      {/* Current Amount */}
      <td className="py-2 px-4 text-right font-mono tabular-nums">
        {amount !== undefined && amount !== null && (
          <span className={cn(amount < 0 && "text-rose-600 dark:text-rose-400")}>
            {formatCurrency(amount)}
          </span>
        )}
      </td>
      
      {/* Previous Amount (if comparison) */}
      {showComparison && (
        <>
          <td className="py-2 px-4 text-right font-mono tabular-nums text-muted-foreground">
            {previousAmount !== undefined && previousAmount !== null && formatCurrency(previousAmount)}
          </td>
          
          {/* $ Change */}
          <td className="py-2 px-4 text-right font-mono tabular-nums">
            {change && change.amount !== undefined && (
              <span className={cn(
                change.amount > 0 && "text-emerald-600 dark:text-emerald-400",
                change.amount < 0 && "text-rose-600 dark:text-rose-400"
              )}>
                {change.amount >= 0 ? '+' : ''}{formatCurrency(change.amount)}
              </span>
            )}
          </td>
          
          {/* % Change */}
          <td className="py-2 px-4 text-right font-mono tabular-nums">
            {change && change.percent !== null && (
              <span className={cn(
                "flex items-center justify-end gap-1",
                change.percent > 0 && "text-emerald-600 dark:text-emerald-400",
                change.percent < 0 && "text-rose-600 dark:text-rose-400"
              )}>
                {change.percent > 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : change.percent < 0 ? (
                  <TrendingDown className="w-3 h-3" />
                ) : null}
                {change.percent >= 0 ? '+' : ''}{change.percent.toFixed(1)}%
              </span>
            )}
          </td>
        </>
      )}
    </tr>
  );
};

// Main Reports Component
export default function Reports() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportRef = useRef(null);
  
  // View state
  const [activeReport, setActiveReport] = useState(searchParams.get('report') || null);
  
  // Report controls
  const [startDate, setStartDate] = useState(startOfYear(new Date()));
  const [endDate, setEndDate] = useState(new Date());
  const [asOfDate, setAsOfDate] = useState(new Date());
  const [basis, setBasis] = useState('cash');
  const [comparisonMode, setComparisonMode] = useState('none');
  const [showSubAccounts, setShowSubAccounts] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  // Tax-mapping inline AI suggest state
  const [suggestAccountIds, setSuggestAccountIds] = useState(null);
  
  // Report data
  const [reportData, setReportData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // AI Narration
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeExpanded, setNarrativeExpanded] = useState(true);

  // Report categories
  const reportCategories = [
    {
      title: 'Financial Statements',
      description: 'Core reports for banks, investors, and tax prep',
      reports: [
        { id: 'profit-loss', title: 'Profit & Loss', description: 'Income and expenses for a period', icon: TrendingUp, color: 'emerald' },
        { id: 'balance-sheet', title: 'Balance Sheet', description: 'Assets, liabilities, and equity', icon: Scale, color: 'blue' },
        { id: 'cash-flow', title: 'Cash Flow Statement', description: 'Cash inflows and outflows', icon: DollarSign, color: 'violet' }
      ]
    },
    {
      title: 'Receivables & Payables',
      description: 'Track who owes you and who you owe',
      reports: [
        { id: 'ar-aging', title: 'AR Aging', description: 'Outstanding customer invoices', icon: Users, color: 'amber' },
        { id: 'ap-aging', title: 'AP Aging', description: 'Outstanding vendor bills', icon: CreditCard, color: 'rose' },
        { id: 'revenue-customer', title: 'Revenue by Customer', description: 'Top customers and income sources', icon: Users, color: 'emerald' }
      ]
    },
    {
      title: 'Expenses & Taxes',
      description: 'Where your money goes',
      reports: [
        { id: 'expense-category', title: 'Expense by Category', description: 'Spending by payroll, rent, utilities, etc.', icon: PieChart, color: 'violet' },
        { id: 'expense-vendor', title: 'Expense by Vendor', description: 'Spending breakdown by vendor name', icon: BarChart3, color: 'amber' },
        { id: 'payroll-summary', title: 'Payroll Summary', description: 'Employee costs and payroll vendors', icon: Users, color: 'blue' },
        { id: 'tax-liability', title: 'Tax Liability', description: 'Estimated tax obligations and payments', icon: FileText, color: 'rose' },
        { id: 'tax-mapping', title: 'Tax Mapping', description: 'Activity by tax-form line (e.g. Schedule C)', icon: FileText, color: 'emerald' }
      ]
    },
    {
      title: 'Ledger & Audit',
      description: 'Detailed transaction records for CPA review',
      reports: [
        { id: 'general-ledger', title: 'General Ledger', description: 'All transactions by account', icon: FileText, color: 'blue' },
        { id: 'trial-balance', title: 'Trial Balance', description: 'Debit and credit balances', icon: CheckCircle2, color: 'emerald' }
      ]
    }
  ];

  // Fetch report data
  const fetchReport = useCallback(async () => {
    if (!currentWorkspace || !activeReport) return;
    
    setLoading(true);
    try {
      let url;
      let compUrl;
      
      // Determine comparison dates
      let compStartDate, compEndDate, compAsOfDate;
      if (comparisonMode === 'prior_period') {
        const periodLength = endDate.getTime() - startDate.getTime();
        compStartDate = new Date(startDate.getTime() - periodLength);
        compEndDate = new Date(endDate.getTime() - periodLength);
        compAsOfDate = subMonths(asOfDate, 1);
      } else if (comparisonMode === 'prior_year') {
        compStartDate = subYears(startDate, 1);
        compEndDate = subYears(endDate, 1);
        compAsOfDate = subYears(asOfDate, 1);
      }
      
      switch (activeReport) {
        case 'profit-loss':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/profit-loss?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}&basis=${basis}`;
          if (comparisonMode !== 'none') {
            compUrl = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/profit-loss?start_date=${format(compStartDate, 'yyyy-MM-dd')}&end_date=${format(compEndDate, 'yyyy-MM-dd')}&basis=${basis}`;
          }
          break;
        case 'balance-sheet':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/balance-sheet?as_of_date=${format(asOfDate, 'yyyy-MM-dd')}&basis=${basis}`;
          if (comparisonMode !== 'none') {
            compUrl = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/balance-sheet?as_of_date=${format(compAsOfDate, 'yyyy-MM-dd')}&basis=${basis}`;
          }
          break;
        case 'cash-flow':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/cash-flow?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          if (comparisonMode !== 'none') {
            compUrl = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/cash-flow?start_date=${format(compStartDate, 'yyyy-MM-dd')}&end_date=${format(compEndDate, 'yyyy-MM-dd')}`;
          }
          break;
        case 'trial-balance':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/trial-balance?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'general-ledger':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/general-ledger?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'ar-aging':
          url = `${API_URL}/api/reports/ar-aging?workspace_id=${currentWorkspace.workspace_id}&as_of_date=${format(asOfDate, 'yyyy-MM-dd')}`;
          break;
        case 'ap-aging':
          url = `${API_URL}/api/reports/ap-aging?workspace_id=${currentWorkspace.workspace_id}&as_of_date=${format(asOfDate, 'yyyy-MM-dd')}`;
          break;
        case 'expense-vendor':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/expense-by-vendor?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'revenue-customer':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/revenue-by-customer?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'expense-category':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/expense-by-category?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'payroll-summary':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/payroll-summary?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'tax-liability':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/tax-liability?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        case 'tax-mapping':
          url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/tax-mapping?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
          break;
        default:
          return;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      }
      
      // Fetch comparison data if needed
      if (compUrl) {
        const compResponse = await fetch(compUrl);
        if (compResponse.ok) {
          const compData = await compResponse.json();
          setComparisonData(compData);
        }
      } else {
        setComparisonData(null);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, activeReport, startDate, endDate, asOfDate, comparisonMode, basis]);

  // Fetch AI narration for supported reports
  const fetchNarration = useCallback(async () => {
    if (!currentWorkspace || !activeReport) return;
    if (!['profit-loss', 'balance-sheet', 'cash-flow'].includes(activeReport)) {
      setNarrative(null);
      return;
    }
    
    setNarrativeLoading(true);
    try {
      let params = '';
      if (['profit-loss', 'cash-flow'].includes(activeReport)) {
        params = `start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
      } else {
        params = `as_of_date=${format(asOfDate, 'yyyy-MM-dd')}`;
      }
      
      const url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reports/${activeReport}/narrate?${params}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setNarrative(data.narrative || null);
      } else {
        setNarrative(null);
      }
    } catch (error) {
      console.error('Failed to fetch narration:', error);
      setNarrative(null);
    } finally {
      setNarrativeLoading(false);
    }
  }, [currentWorkspace, activeReport, startDate, endDate, asOfDate]);

  useEffect(() => {
    if (activeReport) {
      fetchReport();
      setNarrative(null);
    }
  }, [fetchReport, activeReport]);

  // Fetch narration after report data loads
  useEffect(() => {
    if (reportData && ['profit-loss', 'balance-sheet', 'cash-flow'].includes(activeReport)) {
      fetchNarration();
    }
  }, [reportData, activeReport, fetchNarration]);

  // Update URL when report changes
  useEffect(() => {
    if (activeReport) {
      setSearchParams({ report: activeReport });
    } else {
      setSearchParams({});
    }
  }, [activeReport, setSearchParams]);

  const handleExport = (exportFormat) => {
    if (!reportData) {
      toast.error('No report data to export');
      return;
    }
    
    const companyName = currentWorkspace?.name || 'Company';
    
    try {
      // CSV handler — generic across all report types
      if (exportFormat === 'csv') {
        let csvHeaders = [];
        let csvRows = [];
        let csvFilename = 'report.csv';
        let reportTitle = '';
        let dateLabel = '';

        switch (activeReport) {
          case 'profit-loss': {
            reportTitle = 'Profit & Loss';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Account', 'Amount'];
            const incRows = (reportData.income?.rows || reportData.income || []).map(a => [a.account_name || a.name || '', a.balance || a.amount || 0]);
            const expRows = (reportData.expenses?.rows || reportData.expenses || []).map(a => [a.account_name || a.name || '', a.balance || a.amount || 0]);
            csvRows = [['INCOME', ''], ...incRows, ['Total Income', reportData.income?.total || reportData.total_income || 0], ['', ''], ['EXPENSES', ''], ...expRows, ['Total Expenses', reportData.expenses?.total || reportData.total_expenses || 0], ['', ''], ['NET INCOME', reportData.net_income || 0]];
            csvFilename = `ProfitLoss_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'balance-sheet': {
            reportTitle = 'Balance Sheet';
            dateLabel = `As of ${format(asOfDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Account', 'Balance'];
            const assets = (reportData.assets?.rows || reportData.assets || []).map(a => [a.account_name || a.name || '', a.balance || 0]);
            const liabs = (reportData.liabilities?.rows || reportData.liabilities || []).map(a => [a.account_name || a.name || '', a.balance || 0]);
            const eq = (reportData.equity?.rows || reportData.equity || []).map(a => [a.account_name || a.name || '', a.balance || 0]);
            csvRows = [['ASSETS', ''], ...assets, ['Total Assets', reportData.assets?.total || reportData.total_assets || 0], ['', ''], ['LIABILITIES', ''], ...liabs, ['Total Liabilities', reportData.liabilities?.total || reportData.total_liabilities || 0], ['', ''], ['EQUITY', ''], ...eq, ['Total Equity', reportData.equity?.total || reportData.total_equity || 0], ['', ''], ['TOTAL LIABILITIES & EQUITY', (reportData.liabilities?.total || reportData.total_liabilities || 0) + (reportData.equity?.total || reportData.total_equity || 0)]];
            csvFilename = `BalanceSheet_${format(asOfDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'ar-aging':
          case 'ap-aging': {
            const isAR = activeReport === 'ar-aging';
            reportTitle = isAR ? 'Accounts Receivable Aging' : 'Accounts Payable Aging';
            dateLabel = `As of ${format(asOfDate, 'MM/dd/yyyy')}`;
            csvHeaders = [isAR ? 'Customer' : 'Vendor', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total'];
            const items = isAR ? (reportData.customers || []) : (reportData.vendors || []);
            csvRows = items.map(i => [i.customer_name || i.vendor_name || '', i.current || 0, i['1_30'] || 0, i['31_60'] || 0, i['61_90'] || 0, i.over_90 || 0, i.total || 0]);
            const totals = reportData.totals || {};
            csvRows.push(['TOTAL', totals.current || 0, totals['1_30'] || 0, totals['31_60'] || 0, totals['61_90'] || 0, totals.over_90 || 0, totals.total || 0]);
            csvFilename = `${isAR ? 'AR' : 'AP'}Aging_${format(asOfDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'trial-balance': {
            reportTitle = 'Trial Balance';
            dateLabel = `As of ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Code', 'Account Name', 'Debit', 'Credit'];
            const accts = reportData.accounts || reportData.rows || [];
            csvRows = accts.map(a => [a.account_code || a.code || '', a.account_name || a.name || '', a.ending_debit || a.debit_balance || a.debit || '', a.ending_credit || a.credit_balance || a.credit || '']);
            csvRows.push(['', 'TOTALS', reportData.totals?.ending_debit || reportData.total_debits || 0, reportData.totals?.ending_credit || reportData.total_credits || 0]);
            csvFilename = `TrialBalance_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'general-ledger': {
            reportTitle = 'General Ledger';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Date', 'Entry #', 'Account Code', 'Account Name', 'Description', 'Debit', 'Credit'];
            csvRows = (reportData.entries || []).map(e => [e.txn_date || '', e.entry_no || '', e.account_code || '', e.account_name || '', e.memo || e.description || '', e.debit || '', e.credit || '']);
            csvFilename = `GeneralLedger_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'cash-flow': {
            reportTitle = 'Cash Flow Statement';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Description', 'Amount'];
            csvRows = [['OPERATING ACTIVITIES', ''], ...(reportData.operating || []).map(i => [i.description || i.name || '', i.amount || 0]), ['Net Cash from Operations', reportData.net_operating || 0], ['', ''], ['INVESTING ACTIVITIES', ''], ...(reportData.investing || []).map(i => [i.description || i.name || '', i.amount || 0]), ['Net Cash from Investing', reportData.net_investing || 0], ['', ''], ['FINANCING ACTIVITIES', ''], ...(reportData.financing || []).map(i => [i.description || i.name || '', i.amount || 0]), ['Net Cash from Financing', reportData.net_financing || 0], ['', ''], ['NET CHANGE IN CASH', reportData.net_change || 0]];
            csvFilename = `CashFlow_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'expense-vendor': {
            reportTitle = 'Expense by Vendor';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Vendor', 'Amount', '% of Total'];
            const vendors = reportData.vendors || reportData.data || [];
            csvRows = vendors.map(v => [v.vendor_name || v.name || '', v.amount || v.total || 0, ((v.percentage || 0) * 100).toFixed(1) + '%']);
            csvRows.push(['TOTAL', reportData.total || 0, '100%']);
            csvFilename = `ExpenseByVendor_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'revenue-customer': {
            reportTitle = 'Revenue by Customer';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Customer', 'Revenue', '% of Total'];
            const customers = reportData.customers || reportData.data || [];
            csvRows = customers.map(c => [c.customer_name || c.name || '', c.revenue || c.amount || c.total || 0, ((c.percentage || 0) * 100).toFixed(1) + '%']);
            csvRows.push(['TOTAL', reportData.total_revenue || reportData.total || 0, '100%']);
            csvFilename = `RevenueByCustomer_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'expense-category': {
            reportTitle = 'Expense by Category';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Category', 'Amount', '% of Total'];
            const cats = reportData.categories || reportData.data || [];
            csvRows = cats.map(c => [c.category_name || c.name || '', c.amount || c.total || 0, ((c.percentage || 0) * 100).toFixed(1) + '%']);
            csvRows.push(['TOTAL', reportData.total_expenses || reportData.total || 0, '100%']);
            csvFilename = `ExpenseByCategory_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'payroll-summary': {
            reportTitle = 'Payroll Summary';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Category', 'Amount'];
            const payItems = reportData.items || reportData.categories || reportData.data || [];
            csvRows = payItems.map(p => [p.name || p.category_name || '', p.amount || p.total || 0]);
            csvRows.push(['TOTAL', reportData.total || reportData.total_payroll || 0]);
            csvFilename = `PayrollSummary_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'tax-liability': {
            reportTitle = 'Tax Liability';
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Tax Type', 'Estimated Liability'];
            const taxItems = reportData.items || reportData.taxes || reportData.data || [];
            csvRows = taxItems.map(t => [t.name || t.tax_type || '', t.amount || t.liability || 0]);
            csvRows.push(['TOTAL', reportData.total || reportData.total_tax || 0]);
            csvFilename = `TaxLiability_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          case 'tax-mapping': {
            reportTitle = `Tax Mapping — ${reportData.form_name || ''}`;
            dateLabel = `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
            csvHeaders = ['Section', 'Line', 'Description', 'Category', 'Account Code', 'Account Name', 'Amount'];
            csvRows = [];
            for (const section of (reportData.sections || [])) {
              for (const line of (section.lines || [])) {
                if ((line.accounts || []).length === 0) {
                  csvRows.push([section.name, line.line, line.description, line.category, '', '', '']);
                } else {
                  for (const a of line.accounts) {
                    csvRows.push([section.name, line.line, line.description, line.category, a.account_code || '', a.account_name, a.amount]);
                  }
                }
              }
              csvRows.push([section.name + ' TOTAL', '', '', '', '', '', section.section_total || 0]);
            }
            csvRows.push(['GRAND TOTAL', '', '', '', '', 'Net', reportData.net_profit || 0]);
            if ((reportData.unmapped_accounts || []).length > 0) {
              csvRows.push(['', '', '', '', '', '', '']);
              csvRows.push(['UNMAPPED ACCOUNTS', '', '', '', '', '', '']);
              for (const a of reportData.unmapped_accounts) {
                csvRows.push(['Unmapped', '', '', a.account_type, a.account_code || '', a.account_name, a.amount]);
              }
            }
            csvFilename = `TaxMapping_${(reportData.form_type || 'form')}_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
            break;
          }
          default:
            toast.error('CSV export not available for this report');
            return;
        }

        exportReportToCSV({ companyName, reportTitle, dateLabel, headers: csvHeaders, rows: csvRows, filename: csvFilename });
        toast.success('Report exported as CSV');
        return;
      }

      // PDF and Excel handlers
      switch (activeReport) {
        case 'profit-loss':
          if (exportFormat === 'pdf') {
            exportPnLToPDF({
              companyName,
              startDate: startDate,
              endDate: endDate,
              basis: basis,
              incomeAccounts: reportData.income?.rows || reportData.income || [],
              expenseAccounts: reportData.expenses?.rows || reportData.expenses || [],
              totalIncome: reportData.income?.total || reportData.total_income || 0,
              totalExpenses: reportData.expenses?.total || reportData.total_expenses || 0,
              netIncome: reportData.net_income || 0
            });
          } else {
            exportPnLToExcel({
              companyName,
              startDate: startDate,
              endDate: endDate,
              basis: basis,
              incomeAccounts: reportData.income?.rows || reportData.income || [],
              expenseAccounts: reportData.expenses?.rows || reportData.expenses || [],
              totalIncome: reportData.income?.total || reportData.total_income || 0,
              totalExpenses: reportData.expenses?.total || reportData.total_expenses || 0,
              netIncome: reportData.net_income || 0
            });
          }
          break;
          
        case 'balance-sheet':
          if (exportFormat === 'pdf') {
            exportBalanceSheetToPDF({
              companyName,
              asOfDate: asOfDate,
              basis: basis,
              assets: reportData.assets?.rows || reportData.assets || [],
              liabilities: reportData.liabilities?.rows || reportData.liabilities || [],
              equity: reportData.equity?.rows || reportData.equity || [],
              totalAssets: reportData.assets?.total || reportData.total_assets || 0,
              totalLiabilities: reportData.liabilities?.total || reportData.total_liabilities || 0,
              totalEquity: reportData.equity?.total || reportData.total_equity || 0
            });
          } else {
            exportBalanceSheetToExcel({
              companyName,
              asOfDate: asOfDate,
              basis: basis,
              assets: reportData.assets?.rows || reportData.assets || [],
              liabilities: reportData.liabilities?.rows || reportData.liabilities || [],
              equity: reportData.equity?.rows || reportData.equity || [],
              totalAssets: reportData.assets?.total || reportData.total_assets || 0,
              totalLiabilities: reportData.liabilities?.total || reportData.total_liabilities || 0,
              totalEquity: reportData.equity?.total || reportData.total_equity || 0
            });
          }
          break;
          
        case 'ar-aging':
          if (exportFormat === 'pdf') {
            exportAgingToPDF({
              companyName,
              reportTitle: 'Accounts Receivable Aging',
              asOfDate: asOfDate,
              data: reportData.customers || [],
              totals: reportData.totals || {},
              type: 'ar'
            });
          } else {
            exportAgingToExcel({
              companyName,
              reportTitle: 'Accounts Receivable Aging',
              asOfDate: asOfDate,
              data: reportData.customers || [],
              totals: reportData.totals || {},
              type: 'ar'
            });
          }
          break;
          
        case 'ap-aging':
          if (exportFormat === 'pdf') {
            exportAgingToPDF({
              companyName,
              reportTitle: 'Accounts Payable Aging',
              asOfDate: asOfDate,
              data: reportData.vendors || [],
              totals: reportData.totals || {},
              type: 'ap'
            });
          } else {
            exportAgingToExcel({
              companyName,
              reportTitle: 'Accounts Payable Aging',
              asOfDate: asOfDate,
              data: reportData.vendors || [],
              totals: reportData.totals || {},
              type: 'ap'
            });
          }
          break;
          
        case 'trial-balance':
          if (exportFormat === 'pdf') {
            exportTrialBalanceToPDF({
              companyName,
              asOfDate: endDate,
              accounts: reportData.accounts || reportData.rows || [],
              totalDebits: reportData.totals?.ending_debit || reportData.total_debits || 0,
              totalCredits: reportData.totals?.ending_credit || reportData.total_credits || 0
            });
          } else {
            exportTrialBalanceToExcel({
              companyName,
              asOfDate: endDate,
              accounts: reportData.accounts || reportData.rows || [],
              totalDebits: reportData.totals?.ending_debit || reportData.total_debits || 0,
              totalCredits: reportData.totals?.ending_credit || reportData.total_credits || 0
            });
          }
          break;
          
        case 'general-ledger':
          if (exportFormat === 'pdf') {
            exportGeneralLedgerToPDF({
              companyName,
              startDate: startDate,
              endDate: endDate,
              entries: reportData.entries || []
            });
          } else {
            exportGeneralLedgerToExcel({
              companyName,
              startDate: startDate,
              endDate: endDate,
              entries: reportData.entries || []
            });
          }
          break;
          
        case 'cash-flow':
          if (exportFormat === 'pdf') {
            exportCashFlowToPDF({
              companyName,
              startDate: startDate,
              endDate: endDate,
              operating: reportData.operating || [],
              investing: reportData.investing || [],
              financing: reportData.financing || [],
              netOperating: reportData.net_operating || 0,
              netInvesting: reportData.net_investing || 0,
              netFinancing: reportData.net_financing || 0,
              netChange: reportData.net_change || 0
            });
          } else {
            exportCashFlowToExcel({
              companyName,
              startDate: startDate,
              endDate: endDate,
              operating: reportData.operating || [],
              investing: reportData.investing || [],
              financing: reportData.financing || [],
              netOperating: reportData.net_operating || 0,
              netInvesting: reportData.net_investing || 0,
              netFinancing: reportData.net_financing || 0,
              netChange: reportData.net_change || 0
            });
          }
          break;
          
        case 'expense-vendor':
          if (exportFormat === 'pdf') {
            exportExpenseByVendorToPDF({
              companyName,
              startDate: startDate,
              endDate: endDate,
              data: reportData.vendors || reportData.data || reportData || [],
              total: reportData.total || 0
            });
          } else {
            exportExpenseByVendorToExcel({
              companyName,
              startDate: startDate,
              endDate: endDate,
              data: reportData.vendors || reportData.data || reportData || [],
              total: reportData.total || 0
            });
          }
          break;

        case 'revenue-customer':
        case 'expense-category':
        case 'payroll-summary':
        case 'tax-liability':
        case 'tax-mapping': {
          // Generic table export for the remaining report types
          const reportTitles = {
            'revenue-customer': 'Revenue by Customer',
            'expense-category': 'Expense by Category',
            'payroll-summary': 'Payroll Summary',
            'tax-liability': 'Tax Liability',
            'tax-mapping': `Tax Mapping — ${reportData?.form_name || ''}`,
          };
          const headerSets = {
            'revenue-customer': ['Customer', 'Revenue', '% of Total'],
            'expense-category': ['Category', 'Amount', '% of Total'],
            'payroll-summary': ['Category', 'Amount'],
            'tax-liability': ['Tax Type', 'Estimated Liability'],
            'tax-mapping': ['Section', 'Line', 'Description', 'Amount'],
          };
          const dataExtractors = {
            'revenue-customer': () => (reportData.customers || reportData.data || []).map(c => [c.customer_name || c.name || '', c.revenue || c.amount || c.total || 0, ((c.percentage || 0) * 100).toFixed(1) + '%']),
            'expense-category': () => (reportData.categories || reportData.data || []).map(c => [c.category_name || c.name || '', c.amount || c.total || 0, ((c.percentage || 0) * 100).toFixed(1) + '%']),
            'payroll-summary': () => (reportData.items || reportData.categories || reportData.data || []).map(p => [p.name || p.category_name || '', p.amount || p.total || 0]),
            'tax-liability': () => (reportData.items || reportData.taxes || reportData.data || []).map(t => [t.name || t.tax_type || '', t.amount || t.liability || 0]),
            'tax-mapping': () => {
              const rows = [];
              for (const section of (reportData.sections || [])) {
                for (const line of (section.lines || [])) {
                  rows.push([section.name, line.line, line.description, line.total]);
                }
                rows.push([section.name + ' Total', '', '', section.section_total || 0]);
              }
              return rows;
            },
          };

          const title = reportTitles[activeReport];
          const headers = headerSets[activeReport];
          const tableData = dataExtractors[activeReport]();
          const dateStr = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;

          if (exportFormat === 'pdf') {
            exportTableToPDF({
              title,
              subtitle: dateStr,
              companyName,
              headers,
              data: tableData,
              filename: `${activeReport}_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`
            });
          } else {
            exportTableToExcel({
              title,
              subtitle: dateStr,
              headers,
              data: tableData,
              filename: `${activeReport}_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`,
              sheetName: title,
              currencyColumns: [1]
            });
          }
          break;
        }
          
        default:
          toast.error('Export not available for this report');
          return;
      }
      
      toast.success(`Report exported as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const handlePrint = () => {
    if (!reportData) {
      toast.error('No report data to print');
      return;
    }
    
    const companyName = currentWorkspace?.name || 'Company';
    
    try {
      let contentHtml = '';
      let title = '';
      let subtitle = '';
      
      switch (activeReport) {
        case 'profit-loss':
          title = 'Profit & Loss Statement';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')} | ${basis === 'cash' ? 'Cash' : 'Accrual'} Basis`;
          contentHtml = generatePrintTableHTML({
            headers: ['Account', 'Amount'],
            data: [
              { isSection: true, cells: ['INCOME', ''] },
              ...(reportData.income?.rows || reportData.income || []).map(acc => ({ cells: [acc.account_name || acc.name, formatCurrency(acc.balance || acc.amount)] })),
              { isTotal: true, cells: ['Total Income', formatCurrency(reportData.income?.total || reportData.total_income || 0)] },
              { cells: ['', ''] },
              { isSection: true, cells: ['EXPENSES', ''] },
              ...(reportData.expenses?.rows || reportData.expenses || []).map(acc => ({ cells: [acc.account_name || acc.name, formatCurrency(acc.balance || acc.amount)] })),
              { isTotal: true, cells: ['Total Expenses', formatCurrency(reportData.expenses?.total || reportData.total_expenses || 0)] }
            ],
            footerRow: ['NET INCOME', formatCurrency(reportData.net_income || 0)]
          });
          break;
          
        case 'balance-sheet':
          title = 'Balance Sheet';
          subtitle = `As of ${format(asOfDate, 'MMM d, yyyy')} | ${basis === 'cash' ? 'Cash' : 'Accrual'} Basis`;
          contentHtml = generatePrintTableHTML({
            headers: ['Account', 'Balance'],
            data: [
              { isSection: true, cells: ['ASSETS', ''] },
              ...(reportData.assets?.rows || reportData.assets || []).map(acc => ({ cells: [acc.account_name || acc.name, formatCurrency(acc.balance)] })),
              { isTotal: true, cells: ['Total Assets', formatCurrency(reportData.assets?.total || reportData.total_assets || 0)] },
              { cells: ['', ''] },
              { isSection: true, cells: ['LIABILITIES', ''] },
              ...(reportData.liabilities?.rows || reportData.liabilities || []).map(acc => ({ cells: [acc.account_name || acc.name, formatCurrency(acc.balance)] })),
              { isTotal: true, cells: ['Total Liabilities', formatCurrency(reportData.liabilities?.total || reportData.total_liabilities || 0)] },
              { cells: ['', ''] },
              { isSection: true, cells: ['EQUITY', ''] },
              ...(reportData.equity?.rows || reportData.equity || []).map(acc => ({ cells: [acc.account_name || acc.name, formatCurrency(acc.balance)] })),
              { isTotal: true, cells: ['Total Equity', formatCurrency(reportData.equity?.total || reportData.total_equity || 0)] }
            ],
            footerRow: ['TOTAL LIABILITIES & EQUITY', formatCurrency((reportData.liabilities?.total || reportData.total_liabilities || 0) + (reportData.equity?.total || reportData.total_equity || 0))]
          });
          break;
          
        case 'ar-aging':
          title = 'Accounts Receivable Aging';
          subtitle = `As of ${format(new Date(asOfDate), 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Customer', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total'],
            data: (reportData.customers || []).map(c => ({
              cells: [
                c.customer_name,
                formatCurrency(c.current),
                formatCurrency(c['1_30']),
                formatCurrency(c['31_60']),
                formatCurrency(c['61_90']),
                formatCurrency(c.over_90),
                formatCurrency(c.total)
              ]
            })),
            footerRow: [
              'TOTAL',
              formatCurrency(reportData.totals?.current || 0),
              formatCurrency(reportData.totals?.['1_30'] || 0),
              formatCurrency(reportData.totals?.['31_60'] || 0),
              formatCurrency(reportData.totals?.['61_90'] || 0),
              formatCurrency(reportData.totals?.over_90 || 0),
              formatCurrency(reportData.totals?.total || 0)
            ]
          });
          break;
          
        case 'ap-aging':
          title = 'Accounts Payable Aging';
          subtitle = `As of ${format(new Date(asOfDate), 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Vendor', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total'],
            data: (reportData.vendors || []).map(v => ({
              cells: [
                v.vendor_name,
                formatCurrency(v.current),
                formatCurrency(v['1_30']),
                formatCurrency(v['31_60']),
                formatCurrency(v['61_90']),
                formatCurrency(v.over_90),
                formatCurrency(v.total)
              ]
            })),
            footerRow: [
              'TOTAL',
              formatCurrency(reportData.totals?.current || 0),
              formatCurrency(reportData.totals?.['1_30'] || 0),
              formatCurrency(reportData.totals?.['31_60'] || 0),
              formatCurrency(reportData.totals?.['61_90'] || 0),
              formatCurrency(reportData.totals?.over_90 || 0),
              formatCurrency(reportData.totals?.total || 0)
            ]
          });
          break;
          
        case 'trial-balance':
          title = 'Trial Balance';
          subtitle = `As of ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Account Code', 'Account Name', 'Debit', 'Credit'],
            data: (reportData.accounts || reportData.rows || []).map(acc => ({
              cells: [
                acc.account_code || acc.code,
                acc.account_name || acc.name,
                (acc.ending_debit || acc.debit_balance || 0) > 0 ? formatCurrency(acc.ending_debit || acc.debit_balance) : '',
                (acc.ending_credit || acc.credit_balance || 0) > 0 ? formatCurrency(acc.ending_credit || acc.credit_balance) : ''
              ]
            })),
            footerRow: ['', 'TOTALS', formatCurrency(reportData.totals?.ending_debit || reportData.total_debits || 0), formatCurrency(reportData.totals?.ending_credit || reportData.total_credits || 0)]
          });
          break;
          
        case 'general-ledger':
          title = 'General Ledger';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Date', 'Entry #', 'Account', 'Memo', 'Debit', 'Credit'],
            data: (reportData.entries || []).map(e => ({
              cells: [
                e.txn_date,
                e.entry_no,
                `${e.account_code} - ${e.account_name}`,
                e.memo || '',
                e.debit > 0 ? formatCurrency(e.debit) : '',
                e.credit > 0 ? formatCurrency(e.credit) : ''
              ]
            }))
          });
          break;
          
        case 'cash-flow':
          title = 'Cash Flow Statement';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Category', 'Amount'],
            data: [
              { isSection: true, cells: ['OPERATING ACTIVITIES', ''] },
              ...(reportData.operating || []).map(item => ({ cells: [item.description || item.name, formatCurrency(item.amount)] })),
              { isTotal: true, cells: ['Net Cash from Operations', formatCurrency(reportData.net_operating || 0)] },
              { cells: ['', ''] },
              { isSection: true, cells: ['INVESTING ACTIVITIES', ''] },
              ...(reportData.investing || []).map(item => ({ cells: [item.description || item.name, formatCurrency(item.amount)] })),
              { isTotal: true, cells: ['Net Cash from Investing', formatCurrency(reportData.net_investing || 0)] },
              { cells: ['', ''] },
              { isSection: true, cells: ['FINANCING ACTIVITIES', ''] },
              ...(reportData.financing || []).map(item => ({ cells: [item.description || item.name, formatCurrency(item.amount)] })),
              { isTotal: true, cells: ['Net Cash from Financing', formatCurrency(reportData.net_financing || 0)] }
            ],
            footerRow: ['NET CHANGE IN CASH', formatCurrency(reportData.net_change || 0)]
          });
          break;
          
        case 'expense-vendor':
          title = 'Expense by Vendor';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          const vendorData = reportData.data || reportData.vendors || [];
          const vendorTotal = reportData.total || vendorData.reduce((sum, v) => sum + (v.amount || v.total || 0), 0);
          contentHtml = generatePrintTableHTML({
            headers: ['Vendor', 'Amount', '% of Total'],
            data: vendorData.map(item => ({
              cells: [
                item.vendor_name || item.name,
                formatCurrency(item.amount || item.total || 0),
                `${((item.percentage || 0) * 100).toFixed(1)}%`
              ]
            })),
            footerRow: ['TOTAL', formatCurrency(vendorTotal), '100%']
          });
          break;

        case 'revenue-customer':
          title = 'Revenue by Customer';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Customer', 'Revenue', 'Invoices', 'Paid', 'Outstanding', '%'],
            data: (reportData.items || []).map(item => ({
              cells: [
                item.customer_name || item.name,
                formatCurrency(item.revenue || item.amount || 0),
                String(item.invoice_count || 0),
                formatCurrency(item.paid || 0),
                item.outstanding > 0 ? formatCurrency(item.outstanding) : '—',
                `${item.percentage || 0}%`
              ]
            })),
            footerRow: ['TOTAL', formatCurrency(reportData.total || 0), '', '', '', '100%']
          });
          break;

        case 'expense-category':
          title = 'Expense by Category';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Category', 'Amount', 'Entries', '%'],
            data: (reportData.items || []).map(item => ({
              cells: [
                item.category_name || item.name,
                formatCurrency(item.amount || 0),
                String(item.count || 0),
                `${item.percentage || 0}%`
              ]
            })),
            footerRow: ['TOTAL EXPENSES', formatCurrency(reportData.total || 0), '', '100%']
          });
          break;

        case 'payroll-summary':
          title = 'Payroll Summary';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Month', 'Wages', 'Benefits', 'Taxes', 'Fees', 'Total'],
            data: (reportData.monthly || reportData.items || []).map(item => ({
              cells: [
                item.month,
                formatCurrency(item.wages || 0),
                formatCurrency(item.benefits || 0),
                formatCurrency(item.taxes || 0),
                formatCurrency(item.fees || 0),
                formatCurrency(item.total || 0)
              ]
            })),
            footerRow: ['TOTAL', '', '', '', '', formatCurrency(reportData.total || 0)]
          });
          break;

        case 'tax-liability':
          title = 'Tax Liability Summary';
          subtitle = `${format(startDate, 'MMM d, yyyy')} to ${format(endDate, 'MMM d, yyyy')}`;
          contentHtml = generatePrintTableHTML({
            headers: ['Tax Item', 'Code', 'Type', 'Amount'],
            data: (reportData.items || []).map(item => ({
              cells: [
                item.name,
                item.code || '—',
                item.type === 'expense' ? 'Expense' : item.type === 'liability' ? 'Liability' : 'Payment',
                formatCurrency(item.amount || 0)
              ]
            })),
            footerRow: ['TOTAL', '', '', formatCurrency(reportData.total || 0)]
          });
          break;
          
        default:
          toast.error('Print not available for this report');
          return;
      }
      
      printReport({
        title,
        subtitle,
        companyName,
        contentHtml
      });
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Render report hub
  if (!activeReport) {
    return (
      <div className="space-y-8" data-testid="reports-hub">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Financial Reports</h1>
            <p className="text-muted-foreground">
              Generate CPA-grade financial statements and reports
            </p>
          </div>
          <HelpPanel {...reportsHelp} />
        </div>

        {reportCategories.map((category) => (
          <div key={category.title}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold">{category.title}</h2>
              <p className="text-sm text-muted-foreground">{category.description}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.reports.map((report) => (
                <ReportCard
                  key={report.id}
                  icon={report.icon}
                  title={report.title}
                  description={report.description}
                  color={report.color}
                  comingSoon={report.comingSoon}
                  onClick={() => setActiveReport(report.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Get current report info
  const currentReportInfo = reportCategories
    .flatMap(c => c.reports)
    .find(r => r.id === activeReport);

  // Render report viewer
  return (
    <div className="space-y-6" data-testid="report-viewer">
      {/* Back button and title */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setActiveReport(null)}
          data-testid="back-to-reports"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{currentReportInfo?.title || 'Report'}</h1>
          <p className="text-sm text-muted-foreground">{currentReportInfo?.description}</p>
        </div>
      </div>

      {/* Report Toolbar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Range (for period reports) */}
            {['profit-loss', 'general-ledger', 'trial-balance', 'cash-flow'].includes(activeReport) && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Period:</span>
                  
                  {/* Quick Select Dropdown */}
                  <Select onValueChange={(preset) => {
                    const today = new Date();
                    const currentYear = today.getFullYear();
                    
                    switch(preset) {
                      case 'ytd':
                        setStartDate(new Date(currentYear, 0, 1));
                        setEndDate(today);
                        break;
                      case 'q1':
                        setStartDate(new Date(currentYear, 0, 1));
                        setEndDate(new Date(currentYear, 2, 31));
                        break;
                      case 'q2':
                        setStartDate(new Date(currentYear, 3, 1));
                        setEndDate(new Date(currentYear, 5, 30));
                        break;
                      case 'q3':
                        setStartDate(new Date(currentYear, 6, 1));
                        setEndDate(new Date(currentYear, 8, 30));
                        break;
                      case 'q4':
                        setStartDate(new Date(currentYear, 9, 1));
                        setEndDate(new Date(currentYear, 11, 31));
                        break;
                      case 'this-month':
                        setStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
                        setEndDate(today);
                        break;
                      case 'last-month':
                        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                        setStartDate(lastMonth);
                        setEndDate(new Date(today.getFullYear(), today.getMonth(), 0));
                        break;
                      case 'prior-year':
                        setStartDate(new Date(currentYear - 1, 0, 1));
                        setEndDate(new Date(currentYear - 1, 11, 31));
                        break;
                      case 'prior-ytd':
                        setStartDate(new Date(currentYear - 1, 0, 1));
                        setEndDate(new Date(currentYear - 1, today.getMonth(), today.getDate()));
                        break;
                      case 'last-12':
                        const twelveMonthsAgo = new Date(today);
                        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
                        setStartDate(twelveMonthsAgo);
                        setEndDate(today);
                        break;
                      default:
                        break;
                    }
                  }}>
                    <SelectTrigger className="w-[140px]" data-testid="quick-date-select">
                      <SelectValue placeholder="Quick Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ytd">Current YTD</SelectItem>
                      <SelectItem value="this-month">This Month</SelectItem>
                      <SelectItem value="last-month">Last Month</SelectItem>
                      <SelectItem value="q1">Q1 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="q2">Q2 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="q3">Q3 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="q4">Q4 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="last-12">Last 12 Months</SelectItem>
                      <SelectItem value="prior-year">Prior Year</SelectItem>
                      <SelectItem value="prior-ytd">Prior YTD</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="start-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {format(startDate, 'MMM d, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => date && setStartDate(date)}
                      />
                    </PopoverContent>
                  </Popover>
                  <span className="text-muted-foreground">to</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="end-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {format(endDate, 'MMM d, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => date && setEndDate(date)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Separator orientation="vertical" className="h-8" />
              </>
            )}

            {/* As Of Date (for point-in-time reports like Balance Sheet, AR/AP Aging) */}
            {['balance-sheet', 'ar-aging', 'ap-aging'].includes(activeReport) && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">As of:</span>
                  
                  {/* Quick Select for As Of Date */}
                  <Select onValueChange={(preset) => {
                    const today = new Date();
                    const currentYear = today.getFullYear();
                    
                    switch(preset) {
                      case 'today':
                        setAsOfDate(today);
                        break;
                      case 'yesterday':
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        setAsOfDate(yesterday);
                        break;
                      case 'end-last-month':
                        setAsOfDate(new Date(today.getFullYear(), today.getMonth(), 0));
                        break;
                      case 'end-q1':
                        setAsOfDate(new Date(currentYear, 2, 31));
                        break;
                      case 'end-q2':
                        setAsOfDate(new Date(currentYear, 5, 30));
                        break;
                      case 'end-q3':
                        setAsOfDate(new Date(currentYear, 8, 30));
                        break;
                      case 'end-q4':
                        setAsOfDate(new Date(currentYear, 11, 31));
                        break;
                      case 'end-prior-year':
                        setAsOfDate(new Date(currentYear - 1, 11, 31));
                        break;
                      case 'end-prior-month':
                        const priorMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                        setAsOfDate(new Date(priorMonth.getFullYear(), priorMonth.getMonth() + 1, 0));
                        break;
                      default:
                        break;
                    }
                  }}>
                    <SelectTrigger className="w-[150px]" data-testid="quick-asof-select">
                      <SelectValue placeholder="Quick Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="end-last-month">End of Last Month</SelectItem>
                      <SelectItem value="end-prior-month">End of Prior Month</SelectItem>
                      <SelectItem value="end-q1">End of Q1 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="end-q2">End of Q2 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="end-q3">End of Q3 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="end-q4">End of Q4 ({new Date().getFullYear()})</SelectItem>
                      <SelectItem value="end-prior-year">End of Prior Year</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="as-of-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {format(asOfDate, 'MMM d, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={asOfDate}
                        onSelect={(date) => date && setAsOfDate(date)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Separator orientation="vertical" className="h-8" />
              </>
            )}

            {/* Basis Selector */}
            {['profit-loss', 'balance-sheet'].includes(activeReport) && (
              <>
                <Select value={basis} onValueChange={setBasis}>
                  <SelectTrigger className="w-[120px]" data-testid="basis-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accrual">Accrual</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
                <Separator orientation="vertical" className="h-8" />
              </>
            )}

            {/* Comparison Mode */}
            {['profit-loss', 'balance-sheet'].includes(activeReport) && (
              <>
                <Select value={comparisonMode} onValueChange={setComparisonMode}>
                  <SelectTrigger className="w-[150px]" data-testid="comparison-selector">
                    <SelectValue placeholder="Compare to..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Comparison</SelectItem>
                    <SelectItem value="prior_period">Prior Period</SelectItem>
                    <SelectItem value="prior_year">Prior Year</SelectItem>
                  </SelectContent>
                </Select>
                <Separator orientation="vertical" className="h-8" />
              </>
            )}

            {/* Sub-accounts Toggle */}
            {['profit-loss', 'balance-sheet'].includes(activeReport) && (
              <>
                <div className="flex items-center gap-2">
                  <Switch 
                    id="show-subaccounts" 
                    checked={showSubAccounts}
                    onCheckedChange={setShowSubAccounts}
                  />
                  <Label htmlFor="show-subaccounts" className="text-sm">
                    Show Sub-accounts
                  </Label>
                </div>
                <Separator orientation="vertical" className="h-8" />
              </>
            )}

            {/* Export Options */}
            <div className="ml-auto flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-dropdown">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('excel')} data-testid="export-excel">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export as Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">
                    <Download className="w-4 h-4 mr-2" />
                    Export as CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-button">
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <Card>
          <CardContent className="p-8">
            <div className="space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
            </div>
          </CardContent>
        </Card>
      ) : reportData ? (
        <Card ref={reportRef} className="print:shadow-none print:border-none">
          {/* Report Header */}
          <CardHeader className="text-center border-b print:border-black">
            <div className="space-y-1">
              <p className="text-lg font-semibold">{currentWorkspace?.name}</p>
              <CardTitle className="text-xl">{currentReportInfo?.title}</CardTitle>
              <CardDescription>
                {['profit-loss', 'general-ledger', 'trial-balance', 'cash-flow', 'expense-vendor', 'revenue-customer', 'expense-category', 'payroll-summary', 'tax-liability', 'tax-mapping'].includes(activeReport) 
                  ? `${format(startDate, 'MMMM d, yyyy')} to ${format(endDate, 'MMMM d, yyyy')}`
                  : `As of ${format(asOfDate, 'MMMM d, yyyy')}`
                }
              </CardDescription>
              {basis && ['profit-loss', 'balance-sheet'].includes(activeReport) && (
                <Badge variant="outline" className="mt-2">
                  {basis.charAt(0).toUpperCase() + basis.slice(1)} Basis
                </Badge>
              )}
            </div>
          </CardHeader>

          {/* AI Narrative Summary */}
          {['profit-loss', 'balance-sheet', 'cash-flow'].includes(activeReport) && (
            <div className="mx-4 mt-4 mb-2 print:hidden" data-testid="ai-narrative-section">
              <div
                className={cn(
                  "rounded-lg border transition-all duration-200",
                  "bg-gradient-to-r from-violet-500/5 via-purple-500/5 to-indigo-500/5",
                  "border-violet-500/20"
                )}
              >
                <button
                  onClick={() => setNarrativeExpanded(!narrativeExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  data-testid="ai-narrative-toggle"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-500/10">
                      <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                    </div>
                    <span className="text-sm font-medium text-violet-700 dark:text-violet-300">AI Buck Analysis</span>
                    {narrativeLoading && (
                      <RefreshCw className="w-3.5 h-3.5 text-violet-400 animate-spin" />
                    )}
                  </div>
                  <ChevronDown className={cn(
                    "w-4 h-4 text-muted-foreground transition-transform duration-200",
                    narrativeExpanded && "rotate-180"
                  )} />
                </button>
                {narrativeExpanded && (
                  <div className="px-4 pb-4">
                    {narrativeLoading ? (
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-11/12" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                    ) : narrative ? (
                      <p className="text-sm leading-relaxed text-foreground/80" data-testid="ai-narrative-text">
                        {narrative}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">
                        AI analysis unavailable for this report period.
                      </p>
                    )}
                    {narrative && (
                      <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-violet-500/10">
                        <Sparkles className="w-3 h-3 text-violet-400" />
                        <span className="text-[10px] text-muted-foreground">Powered by AI Buck Intelligence</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); fetchNarration(); }}
                          className="ml-auto text-[10px] text-violet-500 hover:text-violet-600 flex items-center gap-1"
                          data-testid="ai-narrative-refresh"
                        >
                          <RefreshCw className="w-2.5 h-2.5" />
                          Refresh
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Report Table */}
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold">Account</th>
                    <th className="py-3 px-4 text-right font-semibold w-32">
                      {['profit-loss', 'general-ledger'].includes(activeReport) ? 'Current Period' : 'Balance'}
                    </th>
                    {comparisonMode !== 'none' && (
                      <>
                        <th className="py-3 px-4 text-right font-semibold w-32 text-muted-foreground">
                          {comparisonMode === 'prior_period' ? 'Prior Period' : 'Prior Year'}
                        </th>
                        <th className="py-3 px-4 text-right font-semibold w-28">$ Change</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">% Change</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {/* Profit & Loss Report */}
                  {activeReport === 'profit-loss' && reportData && (
                    <>
                      {/* Income Section */}
                      <SectionHeader title="Income" />
                      {(reportData.income?.rows || reportData.income || [])?.map?.((account) => (
                        <ReportRow
                          key={account.account_id}
                          code={showSubAccounts ? account.account_code : null}
                          name={account.account_name}
                          amount={account.amount ?? account.balance}
                          previousAmount={(comparisonData?.income?.rows || comparisonData?.income || [])?.find?.(a => a.account_id === account.account_id)?.amount ?? (comparisonData?.income?.rows || comparisonData?.income || [])?.find?.(a => a.account_id === account.account_id)?.balance}
                          showComparison={comparisonMode !== 'none'}
                        />
                      ))}
                      <ReportRow
                        name="Total Income"
                        amount={reportData.income?.total ?? reportData.total_income ?? 0}
                        previousAmount={comparisonData?.income?.total ?? comparisonData?.total_income}
                        showComparison={comparisonMode !== 'none'}
                        isSubtotal
                      />

                      {/* COGS Section (if exists) */}
                      {((reportData.cogs?.rows || reportData.cogs || [])?.length > 0) && (
                        <>
                          <SectionHeader title="Cost of Goods Sold" />
                          {(reportData.cogs?.rows || reportData.cogs || []).map((account) => (
                            <ReportRow
                              key={account.account_id}
                              code={showSubAccounts ? account.account_code : null}
                              name={account.account_name}
                              amount={account.amount ?? account.balance}
                              previousAmount={(comparisonData?.cogs?.rows || comparisonData?.cogs || [])?.find?.(a => a.account_id === account.account_id)?.amount ?? (comparisonData?.cogs?.rows || comparisonData?.cogs || [])?.find?.(a => a.account_id === account.account_id)?.balance}
                              showComparison={comparisonMode !== 'none'}
                            />
                          ))}
                          <ReportRow
                            name="Total COGS"
                            amount={reportData.cogs?.total ?? reportData.total_cogs ?? 0}
                            previousAmount={comparisonData?.cogs?.total ?? comparisonData?.total_cogs}
                            showComparison={comparisonMode !== 'none'}
                            isSubtotal
                          />
                          <ReportRow
                            name="Gross Profit"
                            amount={(reportData.income?.total ?? reportData.total_income ?? 0) - (reportData.cogs?.total ?? reportData.total_cogs ?? 0)}
                            previousAmount={(comparisonData?.income?.total ?? comparisonData?.total_income ?? 0) - (comparisonData?.cogs?.total ?? comparisonData?.total_cogs ?? 0)}
                            showComparison={comparisonMode !== 'none'}
                            isSubtotal
                          />
                        </>
                      )}

                      {/* Expenses Section */}
                      <SectionHeader title="Operating Expenses" />
                      {(reportData.expenses?.rows || reportData.expenses || [])?.map?.((account) => (
                        <ReportRow
                          key={account.account_id}
                          code={showSubAccounts ? account.account_code : null}
                          name={account.account_name}
                          amount={account.amount ?? account.balance}
                          previousAmount={(comparisonData?.expenses?.rows || comparisonData?.expenses || [])?.find?.(a => a.account_id === account.account_id)?.amount ?? (comparisonData?.expenses?.rows || comparisonData?.expenses || [])?.find?.(a => a.account_id === account.account_id)?.balance}
                          showComparison={comparisonMode !== 'none'}
                        />
                      ))}
                      <ReportRow
                        name="Total Expenses"
                        amount={reportData.expenses?.total ?? reportData.total_expenses ?? 0}
                        previousAmount={comparisonData?.expenses?.total ?? comparisonData?.total_expenses}
                        showComparison={comparisonMode !== 'none'}
                        isSubtotal
                      />

                      {/* Net Income */}
                      <ReportRow
                        name="Net Income"
                        amount={reportData.net_income}
                        previousAmount={comparisonData?.net_income}
                        showComparison={comparisonMode !== 'none'}
                        isGrandTotal
                      />
                    </>
                  )}

                  {/* Balance Sheet Report */}
                  {activeReport === 'balance-sheet' && reportData && (
                    <>
                      {/* Assets Section */}
                      <SectionHeader title="Assets" />
                      {(reportData.assets?.rows || reportData.assets || [])?.map?.((account) => (
                        <ReportRow
                          key={account.account_id}
                          code={showSubAccounts ? account.account_code : null}
                          name={account.account_name}
                          amount={account.amount ?? account.balance}
                          previousAmount={(comparisonData?.assets?.rows || comparisonData?.assets || [])?.find?.(a => a.account_id === account.account_id)?.amount ?? (comparisonData?.assets?.rows || comparisonData?.assets || [])?.find?.(a => a.account_id === account.account_id)?.balance}
                          showComparison={comparisonMode !== 'none'}
                        />
                      ))}
                      <ReportRow
                        name="Total Assets"
                        amount={reportData.assets?.total ?? reportData.total_assets ?? 0}
                        previousAmount={comparisonData?.assets?.total ?? comparisonData?.total_assets}
                        showComparison={comparisonMode !== 'none'}
                        isSubtotal
                      />

                      {/* Liabilities Section */}
                      <SectionHeader title="Liabilities" />
                      {(reportData.liabilities?.rows || reportData.liabilities || [])?.map?.((account) => (
                        <ReportRow
                          key={account.account_id}
                          code={showSubAccounts ? account.account_code : null}
                          name={account.account_name}
                          amount={account.amount ?? account.balance}
                          previousAmount={(comparisonData?.liabilities?.rows || comparisonData?.liabilities || [])?.find?.(a => a.account_id === account.account_id)?.amount ?? (comparisonData?.liabilities?.rows || comparisonData?.liabilities || [])?.find?.(a => a.account_id === account.account_id)?.balance}
                          showComparison={comparisonMode !== 'none'}
                        />
                      ))}
                      <ReportRow
                        name="Total Liabilities"
                        amount={reportData.liabilities?.total ?? reportData.total_liabilities ?? 0}
                        previousAmount={comparisonData?.liabilities?.total ?? comparisonData?.total_liabilities}
                        showComparison={comparisonMode !== 'none'}
                        isSubtotal
                      />

                      {/* Equity Section */}
                      <SectionHeader title="Equity" />
                      {(reportData.equity?.rows || reportData.equity || [])?.map?.((account) => (
                        <ReportRow
                          key={account.account_id}
                          code={showSubAccounts ? account.account_code : null}
                          name={account.account_name}
                          amount={account.amount ?? account.balance}
                          previousAmount={(comparisonData?.equity?.rows || comparisonData?.equity || [])?.find?.(a => a.account_id === account.account_id)?.amount ?? (comparisonData?.equity?.rows || comparisonData?.equity || [])?.find?.(a => a.account_id === account.account_id)?.balance}
                          showComparison={comparisonMode !== 'none'}
                        />
                      ))}
                      <ReportRow
                        name="Total Equity"
                        amount={reportData.equity?.total ?? reportData.total_equity ?? 0}
                        previousAmount={comparisonData?.equity?.total ?? comparisonData?.total_equity}
                        showComparison={comparisonMode !== 'none'}
                        isSubtotal
                      />

                      {/* Total Liabilities & Equity */}
                      <ReportRow
                        name="Total Liabilities & Equity"
                        amount={(reportData.liabilities?.total ?? reportData.total_liabilities ?? 0) + (reportData.equity?.total ?? reportData.total_equity ?? 0)}
                        previousAmount={(comparisonData?.liabilities?.total ?? comparisonData?.total_liabilities ?? 0) + (comparisonData?.equity?.total ?? comparisonData?.total_equity ?? 0)}
                        showComparison={comparisonMode !== 'none'}
                        isGrandTotal
                      />
                    </>
                  )}

                  {/* Trial Balance Report */}
                  {activeReport === 'trial-balance' && reportData && (
                    <>
                      {/* Header row with column labels */}
                      <tr className="bg-muted/30 text-xs uppercase tracking-wide">
                        <td className="py-2 px-4 font-semibold">Account</td>
                        <td colSpan={2} className="py-2 px-4 text-center font-semibold border-l">Beginning Balance</td>
                        <td colSpan={2} className="py-2 px-4 text-center font-semibold border-l">Current Period Activity</td>
                        <td colSpan={2} className="py-2 px-4 text-center font-semibold border-l">Ending Balance</td>
                      </tr>
                      <tr className="bg-muted/20 text-xs">
                        <td className="py-1 px-4"></td>
                        <td className="py-1 px-4 text-right border-l">Debit</td>
                        <td className="py-1 px-4 text-right">Credit</td>
                        <td className="py-1 px-4 text-right border-l">Debit</td>
                        <td className="py-1 px-4 text-right">Credit</td>
                        <td className="py-1 px-4 text-right border-l">Debit</td>
                        <td className="py-1 px-4 text-right">Credit</td>
                      </tr>
                      {reportData.rows?.map((row) => (
                        <tr key={row.account_id} className="hover:bg-muted/20 text-sm">
                          <td className="py-2 px-4">
                            <span className="font-mono text-xs text-muted-foreground mr-2">{row.account_code}</span>
                            {row.account_name}
                          </td>
                          {/* Beginning Balance */}
                          <td className="py-2 px-4 text-right font-mono tabular-nums border-l">
                            {row.prior_debit > 0 ? formatCurrency(row.prior_debit) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {row.prior_credit > 0 ? formatCurrency(row.prior_credit) : '-'}
                          </td>
                          {/* Current Period */}
                          <td className="py-2 px-4 text-right font-mono tabular-nums border-l">
                            {row.current_debit > 0 ? formatCurrency(row.current_debit) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {row.current_credit > 0 ? formatCurrency(row.current_credit) : '-'}
                          </td>
                          {/* Ending Balance */}
                          <td className="py-2 px-4 text-right font-mono tabular-nums border-l">
                            {row.ending_debit > 0 ? formatCurrency(row.ending_debit) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {row.ending_credit > 0 ? formatCurrency(row.ending_credit) : '-'}
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-primary/10 font-bold border-t-2">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums border-l">
                          {formatCurrency(reportData.totals?.prior_debit || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.prior_credit || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums border-l">
                          {formatCurrency(reportData.totals?.current_debit || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.current_credit || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums border-l">
                          {formatCurrency(reportData.totals?.ending_debit || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.ending_credit || 0)}
                        </td>
                      </tr>
                      {/* Balance Check */}
                      <tr className="text-xs">
                        <td colSpan={7} className="py-2 px-4 text-right">
                          {reportData.is_balanced ? (
                            <span className="text-emerald-600 flex items-center justify-end gap-1">
                              <CheckCircle2 className="w-4 h-4" /> Trial Balance is in balance
                            </span>
                          ) : (
                            <span className="text-destructive flex items-center justify-end gap-1">
                              <XCircle className="w-4 h-4" /> Trial Balance is out of balance
                            </span>
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                  
                  {/* Cash Flow Statement */}
                  {activeReport === 'cash-flow' && reportData && (
                    <>
                      {/* Operating Activities */}
                      <SectionHeader title="Operating Activities" />
                      {(reportData.operating || [])?.map?.((item, idx) => (
                        <ReportRow
                          key={`op-${idx}`}
                          name={item.description || item.account_name}
                          amount={item.amount || item.balance}
                          showComparison={false}
                        />
                      ))}
                      <ReportRow
                        name="Net Cash from Operations"
                        amount={reportData.operating_total || 0}
                        isSubtotal
                      />

                      {/* Investing Activities */}
                      <SectionHeader title="Investing Activities" />
                      {(reportData.investing || [])?.map?.((item, idx) => (
                        <ReportRow
                          key={`inv-${idx}`}
                          name={item.description || item.account_name}
                          amount={item.amount || item.balance}
                          showComparison={false}
                        />
                      ))}
                      <ReportRow
                        name="Net Cash from Investing"
                        amount={reportData.investing_total || 0}
                        isSubtotal
                      />

                      {/* Financing Activities */}
                      <SectionHeader title="Financing Activities" />
                      {(reportData.financing || [])?.map?.((item, idx) => (
                        <ReportRow
                          key={`fin-${idx}`}
                          name={item.description || item.account_name}
                          amount={item.amount || item.balance}
                          showComparison={false}
                        />
                      ))}
                      <ReportRow
                        name="Net Cash from Financing"
                        amount={reportData.financing_total || 0}
                        isSubtotal
                      />

                      {/* Summary */}
                      <tr className="bg-muted/50 font-bold border-t-2">
                        <td className="py-3 px-4">Net Change in Cash</td>
                        <td className={cn(
                          "py-3 px-4 text-right font-mono tabular-nums",
                          (reportData.net_change || 0) >= 0 ? "text-emerald-600" : "text-rose-600"
                        )}>
                          {formatCurrency(reportData.net_change || 0)}
                        </td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="py-3 px-4">Beginning Cash Balance</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.beginning_cash || 0)}
                        </td>
                      </tr>
                      <tr className="bg-primary/10 font-bold">
                        <td className="py-3 px-4">Ending Cash Balance</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.ending_cash || 0)}
                        </td>
                      </tr>
                    </>
                  )}

                  {/* General Ledger Report */}
                  {activeReport === 'general-ledger' && reportData && (
                    <>
                      {(reportData.accounts || []).map((account) => (
                        <tbody key={account.account_id}>
                          {/* Account Header */}
                          <tr className="bg-muted/50 border-t">
                            <td colSpan={5} className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-muted-foreground">{account.account_code}</span>
                                <span className="font-semibold">{account.account_name}</span>
                                <Badge variant="outline" className="ml-2 text-xs">{account.account_type}</Badge>
                              </div>
                            </td>
                          </tr>
                          {/* Transactions */}
                          {account.transactions?.map((txn, idx) => (
                            <tr key={`${account.account_id}-${idx}`} className="hover:bg-muted/20 text-sm">
                              <td className="py-2 px-4 text-muted-foreground">{txn.date}</td>
                              <td className="py-2 px-4">{txn.memo || `Entry #${txn.entry_no}`}</td>
                              <td className="py-2 px-4 text-right font-mono tabular-nums">
                                {txn.debit > 0 ? formatCurrency(txn.debit) : '-'}
                              </td>
                              <td className="py-2 px-4 text-right font-mono tabular-nums">
                                {txn.credit > 0 ? formatCurrency(txn.credit) : '-'}
                              </td>
                              <td className="py-2 px-4"></td>
                            </tr>
                          ))}
                          {/* Account Subtotal */}
                          <tr className="bg-muted/30 text-sm font-medium">
                            <td colSpan={2} className="py-2 px-4 text-right">Account Total:</td>
                            <td className="py-2 px-4 text-right font-mono tabular-nums">
                              {formatCurrency(account.total_debit)}
                            </td>
                            <td className="py-2 px-4 text-right font-mono tabular-nums">
                              {formatCurrency(account.total_credit)}
                            </td>
                            <td className="py-2 px-4"></td>
                          </tr>
                        </tbody>
                      ))}
                      {/* Grand Total */}
                      <tr className="bg-primary/10 font-bold border-t-2">
                        <td colSpan={2} className="py-3 px-4 text-right">Grand Total:</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.total_debit || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.total_credit || 0)}
                        </td>
                        <td className="py-3 px-4"></td>
                      </tr>
                      {reportData.accounts?.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-muted-foreground">
                            No transactions found for the selected period.
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* Budget vs Actual Report */}
                  {/* Expense by Vendor Report */}
                  {activeReport === 'expense-vendor' && reportData && (
                    <>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Vendor</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Total Spent</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">Transactions</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">% of Total</th>
                      </tr>
                      {(reportData.vendors || reportData.items || [])?.map?.((vendor, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="py-2 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                                {(vendor.name || vendor.vendor_name || 'Unknown').charAt(0).toUpperCase()}
                              </div>
                              {vendor.name || vendor.vendor_name || 'Unknown Vendor'}
                            </div>
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {formatCurrency(vendor.total || vendor.amount || 0)}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {vendor.count || vendor.transaction_count || 0}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {vendor.percentage 
                              ? `${Math.round(vendor.percentage)}%`
                              : reportData.total_expenses > 0 
                                ? `${Math.round(((vendor.total || vendor.amount || 0) / reportData.total_expenses) * 100)}%`
                                : '—'
                            }
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-muted/50 font-bold border-t-2">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.total_expenses || reportData.total || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {reportData.total_transactions || (reportData.vendors || reportData.items || []).reduce((sum, v) => sum + (v.count || v.transaction_count || 0), 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">100%</td>
                      </tr>
                    </>
                  )}

                  {/* Revenue by Customer Report */}
                  {activeReport === 'revenue-customer' && reportData && (
                    <>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Customer</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Revenue</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">Invoices</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Paid</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Outstanding</th>
                        <th className="py-3 px-4 text-right font-semibold w-20">%</th>
                      </tr>
                      {(reportData.items || []).map((item, idx) => (
                        <tr key={idx} className="hover:bg-muted/30" data-testid={`revenue-customer-row-${idx}`}>
                          <td className="py-2 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                {(item.customer_name || item.name || '?').charAt(0).toUpperCase()}
                              </div>
                              {item.customer_name || item.name}
                            </div>
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums font-medium text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(item.revenue || item.amount || 0)}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{item.invoice_count || 0}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{formatCurrency(item.paid || 0)}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums text-amber-600">
                            {item.outstanding > 0 ? formatCurrency(item.outstanding) : '—'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{item.percentage || 0}%</td>
                        </tr>
                      ))}
                      <tr className="bg-muted/50 font-bold border-t-2">
                        <td className="py-3 px-4">Total ({reportData.total_customers || (reportData.items || []).length} customers)</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(reportData.total_revenue || reportData.total || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {(reportData.items || []).reduce((s, i) => s + (i.invoice_count || 0), 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency((reportData.items || []).reduce((s, i) => s + (i.paid || 0), 0))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums text-amber-600">
                          {formatCurrency((reportData.items || []).reduce((s, i) => s + (i.outstanding || 0), 0))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">100%</td>
                      </tr>
                    </>
                  )}

                  {/* Expense by Category Report */}
                  {activeReport === 'expense-category' && reportData && (
                    <>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Category</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Amount</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">Entries</th>
                        <th className="py-3 px-4 text-right font-semibold w-20">%</th>
                      </tr>
                      {(reportData.items || []).map((item, idx) => (
                        <tr key={idx} className="hover:bg-muted/30" data-testid={`expense-category-row-${idx}`}>
                          <td className="py-2 px-4">
                            <div>
                              <span className="font-medium">{item.category_name || item.name}</span>
                              {item.accounts && item.accounts.length > 0 && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {item.accounts.map(a => `${a.code} ${a.name}`).join(', ')}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums font-medium text-rose-600 dark:text-rose-400">
                            {formatCurrency(item.amount || 0)}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{item.count || 0}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{item.percentage || 0}%</td>
                        </tr>
                      ))}
                      <tr className="bg-muted/50 font-bold border-t-2">
                        <td className="py-3 px-4">Total Expenses</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums text-rose-600 dark:text-rose-400">
                          {formatCurrency(reportData.total_expenses || reportData.total || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {(reportData.items || []).reduce((s, i) => s + (i.count || 0), 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">100%</td>
                      </tr>
                    </>
                  )}

                  {/* Payroll Summary Report */}
                  {activeReport === 'payroll-summary' && reportData && (
                    <>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Month</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Wages</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Benefits</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Taxes</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">Fees</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Total</th>
                      </tr>
                      {(reportData.monthly || reportData.items || []).map((item, idx) => (
                        <tr key={idx} className="hover:bg-muted/30" data-testid={`payroll-row-${idx}`}>
                          <td className="py-2 px-4 font-medium">{item.month}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{formatCurrency(item.wages || 0)}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{formatCurrency(item.benefits || 0)}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{formatCurrency(item.taxes || 0)}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">{formatCurrency(item.fees || 0)}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums font-medium">{formatCurrency(item.total || 0)}</td>
                        </tr>
                      ))}
                      <tr className="bg-muted/50 font-bold border-t-2">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency((reportData.monthly || []).reduce((s, m) => s + (m.wages || 0), 0))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency((reportData.monthly || []).reduce((s, m) => s + (m.benefits || 0), 0))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency((reportData.monthly || []).reduce((s, m) => s + (m.taxes || 0), 0))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency((reportData.monthly || []).reduce((s, m) => s + (m.fees || 0), 0))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.total_payroll || reportData.total || 0)}
                        </td>
                      </tr>
                      <tr className="text-sm text-muted-foreground">
                        <td className="py-2 px-4" colSpan="6">
                          Monthly average: {formatCurrency(reportData.avg_monthly || 0)}
                        </td>
                      </tr>
                      {/* Payroll vendors section */}
                      {(reportData.vendors || []).length > 0 && (
                        <>
                          <tr><td colSpan="6" className="py-4"></td></tr>
                          <tr className="bg-blue-50 dark:bg-blue-900/20">
                            <th className="py-3 px-4 text-left font-semibold" colSpan="4">Payroll Vendor</th>
                            <th className="py-3 px-4 text-right font-semibold">Payments</th>
                            <th className="py-3 px-4 text-right font-semibold">Amount</th>
                          </tr>
                          {(reportData.vendors || []).map((v, idx) => (
                            <tr key={idx} className="hover:bg-muted/30">
                              <td className="py-2 px-4" colSpan="4">{v.vendor_name || v.name}</td>
                              <td className="py-2 px-4 text-right font-mono tabular-nums">{v.count}</td>
                              <td className="py-2 px-4 text-right font-mono tabular-nums font-medium">{formatCurrency(v.amount)}</td>
                            </tr>
                          ))}
                        </>
                      )}
                    </>
                  )}

                  {/* Tax Liability Report */}
                  {activeReport === 'tax-liability' && reportData && (
                    <>
                      {/* Summary cards */}
                      <tr>
                        <td colSpan="4" className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="tax-summary">
                            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
                              <p className="text-xs text-rose-600 dark:text-rose-400">Tax Expense</p>
                              <p className="text-lg font-bold font-mono text-rose-700 dark:text-rose-300">{formatCurrency(reportData.total_tax_expense || 0)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                              <p className="text-xs text-amber-600 dark:text-amber-400">Tax Liability</p>
                              <p className="text-lg font-bold font-mono text-amber-700 dark:text-amber-300">{formatCurrency(reportData.total_tax_liability || 0)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                              <p className="text-xs text-emerald-600 dark:text-emerald-400">Tax Paid</p>
                              <p className="text-lg font-bold font-mono text-emerald-700 dark:text-emerald-300">{formatCurrency(reportData.total_tax_paid || 0)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                              <p className="text-xs text-blue-600 dark:text-blue-400">Effective Rate</p>
                              <p className="text-lg font-bold font-mono text-blue-700 dark:text-blue-300">{reportData.effective_rate || 0}%</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Tax Item</th>
                        <th className="py-3 px-4 text-left font-semibold w-24">Code</th>
                        <th className="py-3 px-4 text-left font-semibold w-24">Type</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Amount</th>
                      </tr>
                      {(reportData.items || []).length === 0 && (
                        <tr>
                          <td colSpan="4" className="py-8 px-4 text-center text-muted-foreground">
                            No tax transactions found for this period. Tax entries will appear when transactions are posted to tax-related GL accounts.
                          </td>
                        </tr>
                      )}
                      {(reportData.items || []).map((item, idx) => (
                        <tr key={idx} className="hover:bg-muted/30" data-testid={`tax-row-${idx}`}>
                          <td className="py-2 px-4">{item.name}</td>
                          <td className="py-2 px-4 font-mono text-sm text-muted-foreground">{item.code || '—'}</td>
                          <td className="py-2 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              item.type === 'expense' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' :
                              item.type === 'liability' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                              'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            }`}>
                              {item.type === 'expense' ? 'Expense' : item.type === 'liability' ? 'Liability' : 'Payment'}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums font-medium">
                            {formatCurrency(item.amount || 0)}
                          </td>
                        </tr>
                      ))}
                      {(reportData.items || []).length > 0 && (
                        <tr className="bg-muted/50 font-bold border-t-2">
                          <td className="py-3 px-4" colSpan="3">Total</td>
                          <td className="py-3 px-4 text-right font-mono tabular-nums">
                            {formatCurrency(reportData.total || 0)}
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* Tax Mapping Report */}
                  {activeReport === 'tax-mapping' && reportData && (
                    <>
                      <tr>
                        <td colSpan="4" className="p-4">
                          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap" data-testid="tax-mapping-header">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-muted-foreground">Form</p>
                              <p className="text-sm font-semibold">{reportData.form_name} <span className="text-muted-foreground font-normal">— {reportData.form_description}</span></p>
                            </div>
                            <a
                              href="/tax-mapper"
                              className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 underline-offset-2 hover:underline"
                              data-testid="tax-mapping-edit-mapping-link"
                            >Edit mapping →</a>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tax-mapping-summary">
                            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                              <p className="text-xs text-emerald-600 dark:text-emerald-400">Total Income</p>
                              <p className="text-lg font-bold font-mono text-emerald-700 dark:text-emerald-300">{formatCurrency(reportData.total_income || 0)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
                              <p className="text-xs text-rose-600 dark:text-rose-400">Total Expenses</p>
                              <p className="text-lg font-bold font-mono text-rose-700 dark:text-rose-300">{formatCurrency(reportData.total_expenses || 0)}</p>
                            </div>
                            <div className={`p-3 rounded-lg border ${reportData.net_profit >= 0 ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
                              <p className={`text-xs ${reportData.net_profit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>Net</p>
                              <p className={`text-lg font-bold font-mono ${reportData.net_profit >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-amber-700 dark:text-amber-300'}`}>{formatCurrency(reportData.net_profit || 0)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                              <p className="text-xs text-violet-600 dark:text-violet-400">Mapping Coverage</p>
                              <p className="text-lg font-bold font-mono text-violet-700 dark:text-violet-300">{reportData.mapping_coverage_pct || 0}%</p>
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold w-20">Line</th>
                        <th className="py-3 px-4 text-left font-semibold">Description / Accounts</th>
                        <th className="py-3 px-4 text-left font-semibold w-24">Category</th>
                        <th className="py-3 px-4 text-right font-semibold w-32">Amount</th>
                      </tr>

                      {(reportData.sections || []).map((section) => (
                        <Fragment key={section.name}>
                          <tr className="bg-muted/20">
                            <td colSpan="4" className="py-2 px-4 font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                              {section.name}
                            </td>
                          </tr>
                          {(section.lines || []).map((line) => (
                            <Fragment key={`${section.name}-${line.line}`}>
                              <tr className="hover:bg-muted/20 border-t" data-testid={`tax-mapping-line-${line.line}`}>
                                <td className="py-2 px-4 font-mono text-sm">{line.line}</td>
                                <td className="py-2 px-4">
                                  <p className="font-medium">{line.description}</p>
                                  {(line.accounts || []).length > 0 && (
                                    <ul className="mt-1 ml-3 text-xs text-muted-foreground space-y-0.5">
                                      {line.accounts.map((a) => (
                                        <li key={a.account_id} className="flex items-center justify-between gap-3">
                                          <span>{a.account_code ? `${a.account_code} · ` : ''}{a.account_name}</span>
                                          <span className="font-mono tabular-nums">{formatCurrency(a.amount)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                                <td className="py-2 px-4">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    line.category === 'income'
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                                  }`}>
                                    {line.category === 'income' ? 'Income' : 'Expense'}
                                  </span>
                                </td>
                                <td className="py-2 px-4 text-right font-mono tabular-nums font-medium">
                                  {line.total !== 0 ? formatCurrency(line.total) : <span className="text-muted-foreground">—</span>}
                                </td>
                              </tr>
                            </Fragment>
                          ))}
                          <tr className="bg-muted/40 border-t-2 font-semibold">
                            <td className="py-2 px-4" colSpan="3">{section.name} Total</td>
                            <td className="py-2 px-4 text-right font-mono tabular-nums">{formatCurrency(section.section_total || 0)}</td>
                          </tr>
                        </Fragment>
                      ))}

                      <tr className="bg-primary/10 font-bold border-t-4">
                        <td className="py-3 px-4" colSpan="3">Net (Income − Expenses)</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">{formatCurrency(reportData.net_profit || 0)}</td>
                      </tr>

                      {(reportData.unmapped_accounts || []).length > 0 && (
                        <tr>
                          <td colSpan="4" className="p-4">
                            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20 p-4" data-testid="tax-mapping-unmapped-block">
                              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                                <div>
                                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                                    {reportData.unmapped_accounts.length} unmapped {reportData.unmapped_accounts.length === 1 ? 'account has' : 'accounts have'} activity
                                  </p>
                                  <p className="text-xs text-amber-700 dark:text-amber-300">These accounts have ledger activity but aren't mapped to any tax-form line yet.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => setSuggestAccountIds(reportData.unmapped_accounts.map((a) => a.account_id))}
                                    className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
                                    data-testid="tax-mapping-ai-suggest-all-btn"
                                  >
                                    ✨ Auto-suggest all
                                  </Button>
                                  <a href="/tax-mapper" className="text-xs font-semibold text-amber-900 dark:text-amber-100 underline-offset-2 hover:underline">
                                    Map manually →
                                  </a>
                                </div>
                              </div>
                              <ul className="mt-2 divide-y divide-amber-200/60 dark:divide-amber-800/60">
                                {reportData.unmapped_accounts.map((a) => (
                                  <li key={a.account_id} className="py-1.5 flex items-center justify-between gap-3 text-sm">
                                    <span className="text-amber-900 dark:text-amber-100">{a.account_code ? `${a.account_code} · ` : ''}{a.account_name}</span>
                                    <div className="flex items-center gap-3">
                                      <span className="font-mono tabular-nums text-amber-900 dark:text-amber-100">{formatCurrency(a.amount)}</span>
                                      <button
                                        onClick={() => setSuggestAccountIds([a.account_id])}
                                        className="text-xs font-semibold text-violet-700 dark:text-violet-300 hover:underline"
                                        data-testid={`tax-mapping-ai-suggest-${a.account_id}`}
                                      >
                                        ✨ Suggest
                                      </button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* AR Aging Report */}
                  {activeReport === 'ar-aging' && reportData && (
                    <>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Customer</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">Current</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">1-30 Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">31-60 Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">61-90 Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">90+ Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Total</th>
                      </tr>
                      {(reportData.customers || []).map((customer, idx) => (
                        <tr key={customer.customer_id || idx} className="hover:bg-muted/20">
                          <td className="py-2 px-4 font-medium">{customer.customer_name}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {customer.current > 0 ? formatCurrency(customer.current) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {customer['1_30'] > 0 ? formatCurrency(customer['1_30']) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {customer['31_60'] > 0 ? formatCurrency(customer['31_60']) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {customer['61_90'] > 0 ? formatCurrency(customer['61_90']) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums text-rose-600">
                            {customer.over_90 > 0 ? formatCurrency(customer.over_90) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums font-medium">
                            {formatCurrency(customer.total)}
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-primary/10 font-bold border-t-2">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.current || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.['1_30'] || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.['31_60'] || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.['61_90'] || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums text-rose-600">
                          {formatCurrency(reportData.totals?.over_90 || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.total || 0)}
                        </td>
                      </tr>
                      {reportData.customers?.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-muted-foreground">
                            No outstanding customer invoices found as of this date.
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* AP Aging Report */}
                  {activeReport === 'ap-aging' && reportData && (
                    <>
                      <tr className="bg-muted/30">
                        <th className="py-3 px-4 text-left font-semibold">Vendor</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">Current</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">1-30 Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">31-60 Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">61-90 Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-24">90+ Days</th>
                        <th className="py-3 px-4 text-right font-semibold w-28">Total</th>
                      </tr>
                      {(reportData.vendors || []).map((vendor, idx) => (
                        <tr key={vendor.vendor_id || idx} className="hover:bg-muted/20">
                          <td className="py-2 px-4 font-medium">{vendor.vendor_name}</td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {vendor.current > 0 ? formatCurrency(vendor.current) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {vendor['1_30'] > 0 ? formatCurrency(vendor['1_30']) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {vendor['31_60'] > 0 ? formatCurrency(vendor['31_60']) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums">
                            {vendor['61_90'] > 0 ? formatCurrency(vendor['61_90']) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums text-rose-600">
                            {vendor.over_90 > 0 ? formatCurrency(vendor.over_90) : '-'}
                          </td>
                          <td className="py-2 px-4 text-right font-mono tabular-nums font-medium">
                            {formatCurrency(vendor.total)}
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-primary/10 font-bold border-t-2">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.current || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.['1_30'] || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.['31_60'] || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.['61_90'] || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums text-rose-600">
                          {formatCurrency(reportData.totals?.over_90 || 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          {formatCurrency(reportData.totals?.total || 0)}
                        </td>
                      </tr>
                      {reportData.vendors?.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-muted-foreground">
                            No outstanding vendor bills found as of this date.
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Report Footer */}
            {activeReport === 'trial-balance' && reportData && (
              <div className="p-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {reportData.is_balanced ? (
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Balanced
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" />
                      Unbalanced - Difference: {formatCurrency(Math.abs((reportData.totals?.ending_debit || 0) - (reportData.totals?.ending_credit || 0)))}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Period: {reportData.start_date} to {reportData.end_date} | Generated on {format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No data available for this report</p>
            <p className="text-sm text-muted-foreground mt-1">Try adjusting the date range or check that transactions exist</p>
          </CardContent>
        </Card>
      )}

      {suggestAccountIds && currentWorkspace && reportData?.form_type && (
        <TaxMapperSuggestionsModal
          workspaceId={currentWorkspace.workspace_id}
          formType={reportData.form_type}
          accountIds={suggestAccountIds}
          formLines={null}
          onClose={() => setSuggestAccountIds(null)}
          onApplied={() => {
            // Re-fetch the report so the unmapped list shrinks
            fetchReport();
          }}
        />
      )}
    </div>
  );
}
